import {
  normalizePersistentWorkspaces,
  type PersistentWorkspaceRegistry,
  type PersistentWorkspaceRegistryUpdate,
} from './persistentWorkspaces';

export type AiWorkspaceSettings = {
  /**
   * Base folder name under Electron `userData`.
   * Example: `sql-studio-ai-workspace`
   */
  folder: string;
  /** Debounce delay for workspace file sync (ms) */
  syncDebounceMs: number;
  /** Show "Force unlock" after this many minutes of inactivity */
  lockTimeoutMinutes: number;
  /** Delete workspace folder when AI terminal closes */
  cleanupOnClose: boolean;
  /** Auto-export schema DDLs when AI workspace opens */
  exportSchemaOnOpen: boolean;
  /**
   * Show a consent dialog (target-database choice + what-gets-exported
   * explanation) before setting up an AI workspace. Independent of
   * `exportSchemaOnOpen` — this gates *whether we ask*, not *whether we export*.
   */
  confirmBeforeSetup: boolean;
};

export type HistorySettings = {
  /** Save execution history to `.history/` */
  enabled: boolean;
  /** Max history entries per workspace */
  maxEntries: number;
  /** Max age for history files (e.g. `7d`, `24h`, `60m`) */
  maxAge: string;
};

export type AgentContextSettings = {
  /** Generate `CLAUDE.md`/`AGENTS.md` */
  enabled: boolean;
  /** Include table/column summary in context files */
  includeSchema: boolean;
};

/** Which agent an opt-in auto-launch should target. See `AutoLaunchSettings`. */
export type AutoLaunchAgentSetting = 'auto' | 'claude' | 'codex';

export type AutoLaunchSettings = {
  /**
   * When true, a freshly opened AI terminal writes the resolved agent's
   * launch command into the pty right after the welcome banner, instead of
   * leaving the user at a blank prompt. Opt-in, defaults off.
   */
  enabled: boolean;
  /**
   * Which agent to launch. `'auto'` resolves from main-process CLI detection
   * alone (never AI Chat's per-tab selection — see
   * `electron/terminal/agentDetection.ts`'s `resolveAutoLaunchAgent`); an
   * explicit `'claude'`/`'codex'` is honoured as written.
   */
  agent: AutoLaunchAgentSetting;
};

export type NudgeSettings = {
  /**
   * User dismissed the "Teach ‹agent› about Jam SQL: run `jam-sql
   * add-skill`" row in the AI terminal context bar (Task T15). One flag for
   * the single dismissible row — whichever detected-but-skill-less agent
   * triggered it — not per-agent.
   */
  addSkillDismissed: boolean;
};

export type AiTerminalSettings = {
  aiWorkspace: AiWorkspaceSettings;
  history: HistorySettings;
  agentContext: AgentContextSettings;
  autoLaunch: AutoLaunchSettings;
  nudges: NudgeSettings;
  persistentWorkspaces: PersistentWorkspaceRegistry;
};

/** Deep partial for setAiTerminalSettings - allows updating individual nested fields */
export type AiTerminalSettingsUpdate = {
  aiWorkspace?: Partial<AiWorkspaceSettings>;
  history?: Partial<HistorySettings>;
  agentContext?: Partial<AgentContextSettings>;
  autoLaunch?: Partial<AutoLaunchSettings>;
  nudges?: Partial<NudgeSettings>;
  /** Entry-level: only the listed connection ids change; `null` removes one. */
  persistentWorkspaces?: PersistentWorkspaceRegistryUpdate;
};

export const defaultAiTerminalSettings: AiTerminalSettings = {
  aiWorkspace: {
    folder: 'sql-studio-ai-workspace',
    syncDebounceMs: 500,
    lockTimeoutMinutes: 5,
    cleanupOnClose: true,
    exportSchemaOnOpen: true,
    confirmBeforeSetup: true,
  },
  history: {
    enabled: true,
    maxEntries: 100,
    maxAge: '7d',
  },
  agentContext: {
    enabled: true,
    includeSchema: true,
  },
  autoLaunch: {
    enabled: false,
    agent: 'auto',
  },
  nudges: {
    addSkillDismissed: false,
  },
  persistentWorkspaces: {},
};

export function parseDurationToMs(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const match = /^(\d+)\s*(ms|s|m|h|d)$/i.exec(trimmed);
  if (!match) return null;

  const amount = Number(match[1]);
  if (!Number.isFinite(amount) || amount < 0) return null;

  const unit = match[2].toLowerCase();
  switch (unit) {
    case 'ms':
      return amount;
    case 's':
      return amount * 1000;
    case 'm':
      return amount * 60 * 1000;
    case 'h':
      return amount * 60 * 60 * 1000;
    case 'd':
      return amount * 24 * 60 * 60 * 1000;
    default:
      return null;
  }
}

export function isValidWorkspaceFolderName(folder: string): boolean {
  const trimmed = folder.trim();
  if (!trimmed) return false;
  if (trimmed.includes('/') || trimmed.includes('\\')) return false;
  if (trimmed.includes('..')) return false;
  return true;
}

/**
 * Persistent-workspace registry rules (types, folder-name derivation,
 * normalization, collision-safe merge) live in their own module — see
 * `persistentWorkspaces.ts`. Re-exported here so every existing importer of
 * `aiTerminalSettings` keeps working and there is still one obvious entry
 * point for AI/terminal settings.
 */
export {
  toWorkspaceFolderSafeName,
  connectionIdDigest,
  persistentWorkspaceFolderName,
  normalizePersistentWorkspaces,
  mergePersistentWorkspaces,
  type PersistentWorkspaceEntry,
  type PersistentWorkspaceRegistry,
  type PersistentWorkspaceRegistryUpdate,
} from './persistentWorkspaces';

export function normalizeAiTerminalSettings(input: unknown): AiTerminalSettings {
  if (!input || typeof input !== 'object') return defaultAiTerminalSettings;

  const record = input as Record<string, unknown>;
  const aiWorkspace = record.aiWorkspace as Record<string, unknown> | undefined;
  const history = record.history as Record<string, unknown> | undefined;
  const agentContext = record.agentContext as Record<string, unknown> | undefined;
  const autoLaunch = record.autoLaunch as Record<string, unknown> | undefined;
  const nudges = record.nudges as Record<string, unknown> | undefined;

  const folder =
    typeof aiWorkspace?.folder === 'string' && isValidWorkspaceFolderName(aiWorkspace.folder)
      ? aiWorkspace.folder.trim()
      : defaultAiTerminalSettings.aiWorkspace.folder;

  const syncDebounceMs =
    typeof aiWorkspace?.syncDebounceMs === 'number' && Number.isFinite(aiWorkspace.syncDebounceMs)
      ? Math.max(0, Math.min(10_000, Math.trunc(aiWorkspace.syncDebounceMs)))
      : defaultAiTerminalSettings.aiWorkspace.syncDebounceMs;

  const lockTimeoutMinutes =
    typeof aiWorkspace?.lockTimeoutMinutes === 'number' && Number.isFinite(aiWorkspace.lockTimeoutMinutes)
      ? Math.max(0, Math.min(60, Math.trunc(aiWorkspace.lockTimeoutMinutes)))
      : defaultAiTerminalSettings.aiWorkspace.lockTimeoutMinutes;

  const cleanupOnClose =
    typeof aiWorkspace?.cleanupOnClose === 'boolean'
      ? aiWorkspace.cleanupOnClose
      : defaultAiTerminalSettings.aiWorkspace.cleanupOnClose;

  const exportSchemaOnOpen =
    typeof aiWorkspace?.exportSchemaOnOpen === 'boolean'
      ? aiWorkspace.exportSchemaOnOpen
      : defaultAiTerminalSettings.aiWorkspace.exportSchemaOnOpen;

  // Absent in a persisted blob from before this field existed ⇒ keep the
  // default (true). Existing users are never silently flipped to no-consent.
  const confirmBeforeSetup =
    typeof aiWorkspace?.confirmBeforeSetup === 'boolean'
      ? aiWorkspace.confirmBeforeSetup
      : defaultAiTerminalSettings.aiWorkspace.confirmBeforeSetup;

  const historyEnabled =
    typeof history?.enabled === 'boolean' ? history.enabled : defaultAiTerminalSettings.history.enabled;

  const maxEntries =
    typeof history?.maxEntries === 'number' && Number.isFinite(history.maxEntries)
      ? Math.max(1, Math.min(10_000, Math.trunc(history.maxEntries)))
      : defaultAiTerminalSettings.history.maxEntries;

  const maxAge =
    typeof history?.maxAge === 'string' && parseDurationToMs(history.maxAge) !== null
      ? history.maxAge.trim()
      : defaultAiTerminalSettings.history.maxAge;

  const agentContextEnabled =
    typeof agentContext?.enabled === 'boolean'
      ? agentContext.enabled
      : defaultAiTerminalSettings.agentContext.enabled;

  const includeSchema =
    typeof agentContext?.includeSchema === 'boolean'
      ? agentContext.includeSchema
      : defaultAiTerminalSettings.agentContext.includeSchema;

  // Absent in a persisted blob from before this field existed ⇒ keep the
  // default (disabled). Same precedent as confirmBeforeSetup above — a new
  // section must never crash on an older blob, and must never turn itself on
  // for a user who never opted in.
  const autoLaunchEnabled =
    typeof autoLaunch?.enabled === 'boolean'
      ? autoLaunch.enabled
      : defaultAiTerminalSettings.autoLaunch.enabled;

  const autoLaunchAgent: AutoLaunchAgentSetting =
    autoLaunch?.agent === 'auto' || autoLaunch?.agent === 'claude' || autoLaunch?.agent === 'codex'
      ? autoLaunch.agent
      : defaultAiTerminalSettings.autoLaunch.agent;

  // Absent in a persisted blob from before this field existed ⇒ keep the
  // default (not dismissed). Same precedent as confirmBeforeSetup/autoLaunch
  // above — a new section must never crash on an older blob.
  const addSkillDismissed =
    typeof nudges?.addSkillDismissed === 'boolean'
      ? nudges.addSkillDismissed
      : defaultAiTerminalSettings.nudges.addSkillDismissed;

  // Absent in a persisted blob from before this field existed ⇒ empty registry
  // (nothing is kept). Same never-crash precedent as the sections above — and
  // the failure direction is deliberate: an unreadable/absent registry means
  // "don't reuse a folder", never "wipe one". Folders are protected on disk by
  // their `.jam-persistent` marker, not by this map. See `workspaceManager`.
  const persistentWorkspaces = normalizePersistentWorkspaces(record.persistentWorkspaces);

  return {
    aiWorkspace: {
      folder,
      syncDebounceMs,
      lockTimeoutMinutes,
      cleanupOnClose,
      exportSchemaOnOpen,
      confirmBeforeSetup,
    },
    history: {
      enabled: historyEnabled,
      maxEntries,
      maxAge,
    },
    agentContext: {
      enabled: agentContextEnabled,
      includeSchema,
    },
    autoLaunch: {
      enabled: autoLaunchEnabled,
      agent: autoLaunchAgent,
    },
    nudges: {
      addSkillDismissed,
    },
    persistentWorkspaces,
  };
}
